import React from 'react';
import {
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormHelperText,
    IconButton,
    InputAdornment,
    InputLabel,
    makeStyles,
    MenuItem,
    Select,
    Slider,
    Switch,
    TextField,
    Typography,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import RemoveIcon from '@material-ui/icons/Remove';
import i18n from 'i18n-js';

import { theme } from 'config/theme';
import { DineInQrCode } from 'api/controllers/tableMaps';
import { canApplyPendingTable, EditorTable, TableShape, useTableMapWizard } from '../context';
import { TABLE_SHAPES, TABLE_SIZE_MIN } from '../mapGeometry';
import {
    isGeometryEdited,
    stepSeats,
    TABLE_NAME_MAX_LENGTH,
    TABLE_SEATS_MAX,
    TABLE_SHAPE_NAME_KEY,
    tableEditOf,
    tableInputErrors,
    TableInputs,
    tableInputsOf,
    TableSizeError,
} from '../tableEdit';

const MAGENTA_DARK = theme.palette.primary.dark;
const ASSIGNED_COLOR = '#2E7D32';

const SIZE_ERROR_KEY: Record<TableSizeError, string> = {
    required: 'tableMaps.wizard.tables.sizeRequired',
    notPositive: 'tableMaps.wizard.tables.sizeNotPositive',
    tooSmall: 'tableMaps.wizard.tables.sizeTooSmall',
    tooLarge: 'tableMaps.wizard.tables.sizeTooLarge',
    largerThanMap: 'tableMaps.wizard.tables.sizeLargerThanMap',
};

const useStyles = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: 14,
        '& .MuiFormHelperText-root': {
            margin: '6px 0 0',
            fontSize: 11,
            lineHeight: 1.4,
        },
    },
    title: {
        fontSize: 13,
        fontWeight: 700,
        color: MAGENTA_DARK,
        letterSpacing: 0.5,
        textTransform: 'uppercase' as const,
    },
    sectionLabel: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginBottom: 6,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: 0.5,
        textTransform: 'uppercase' as const,
        color: theme.palette.text.primary,
    },
    fieldRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        '& > *': {
            flex: '1 1 0',
            minWidth: 0,
        },
    },
    qrLabel: {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        maxWidth: 'calc(133% - 24px)',
    },
    assignedChip: {
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        padding: '1px 6px',
        borderRadius: 10,
        border: `1px solid ${ASSIGNED_COLOR}`,
        color: ASSIGNED_COLOR,
        fontSize: 9,
        fontWeight: 700,
    },
    assignedIcon: {
        fontSize: 12,
    },
    pendingChip: {
        padding: '1px 10px',
        borderRadius: 10,
        backgroundColor: 'rgba(153, 86, 126, 0.12)',
        color: MAGENTA_DARK,
        fontSize: 9,
        fontWeight: 700,
    },
    pendingNote: {
        fontSize: 11,
        fontWeight: 400,
        textTransform: 'none' as const,
        color: theme.palette.text.secondary,
    },
    stepperButton: {
        padding: 2,
    },
    rotationRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
    },
    rotationField: {
        width: 96,
        flexShrink: 0,
    },
    // The 0° / 359° mark labels are centred on the track ends and need half their width as side room.
    sliderBox: {
        flex: 1,
        minWidth: 0,
        padding: '0 16px',
    },
    // Lines the checkbox label up with the wider switch above it.
    lockCheckbox: {
        marginRight: 16,
    },
    // The toggle above already carries its own vertical padding.
    toggleHint: {
        marginTop: '0 !important',
    },
    actions: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: 8,
        '& > *': {
            whiteSpace: 'nowrap' as const,
        },
    },
    deleteButton: {
        alignSelf: 'flex-start',
        color: theme.palette.error.dark,
        borderColor: theme.palette.grey['400'],
        textTransform: 'none' as const,
        fontWeight: 600,
    },
});

interface TableSettingsPanelProps {
    table: EditorTable;
    qrCodes: DineInQrCode[];
    originalActiveQrCodes: string[];
    onDelete: () => void;
}

export default function TableSettingsPanel({
    table,
    qrCodes,
    originalActiveQrCodes,
    onDelete,
}: TableSettingsPanelProps) {
    const classes = useStyles();
    const { draft, dispatch } = useTableMapWizard();
    const pending = draft.pendingTable?.id === table.id ? draft.pendingTable : null;
    const inputs = pending?.inputs ?? tableInputsOf(table, draft.tableDefaults);
    const edit = pending?.edit ?? tableEditOf(table);
    const otherTables = draft.tables.filter((t) => t.id !== table.id);
    const errors = tableInputErrors(inputs, otherTables, draft.size);
    const canApply = !!pending && canApplyPendingTable(draft);
    const isGeometryPending = !!pending && isGeometryEdited(table, edit, draft.tableDefaults);
    const otherUsedQrCodes = otherTables
        .filter((t) => t.isActive && !!t.qrCode)
        .map((t) => t.qrCode as string);
    // isUsed means "used by ANOTHER map's active table". A QR that belonged to
    // this map's original active tables is not used elsewhere — the draft's own
    // otherUsedQrCodes (active-only) is the authoritative in-map source.
    const availableQrCodes = qrCodes.filter(
        (qr) =>
            qr.name === table.qrCode ||
            qr.name === inputs.qrCode ||
            ((!qr.isUsed || originalActiveQrCodes.includes(qr.name)) &&
                !otherUsedQrCodes.includes(qr.name))
    );

    function change(patch: Partial<TableInputs>) {
        dispatch({ type: 'EDIT_PENDING_TABLE', payload: { id: table.id, patch } });
    }

    function sizeMessage(error: TableSizeError | null, mapAxis: number, fieldKey: string) {
        if (!error) return null;
        return (
            <FormHelperText error>
                {i18n.t(SIZE_ERROR_KEY[error], {
                    min: TABLE_SIZE_MIN,
                    max: mapAxis,
                    field: i18n.t(fieldKey),
                })}
            </FormHelperText>
        );
    }

    function renderSeatsStepper() {
        return (
            <InputAdornment position="end">
                <IconButton
                    className={classes.stepperButton}
                    aria-label={i18n.t('tableMaps.wizard.tables.fewerSeats')}
                    onClick={() =>
                        change({ seats: String(stepSeats(inputs.seats, edit.seats, -1)) })
                    }
                >
                    <RemoveIcon fontSize="small" />
                </IconButton>
                <IconButton
                    className={classes.stepperButton}
                    aria-label={i18n.t('tableMaps.wizard.tables.moreSeats')}
                    onClick={() =>
                        change({ seats: String(stepSeats(inputs.seats, edit.seats, 1)) })
                    }
                >
                    <AddIcon fontSize="small" />
                </IconButton>
            </InputAdornment>
        );
    }

    return (
        <div className={classes.root}>
            <Typography className={classes.title}>
                {i18n.t('tableMaps.wizard.tables.settingsTitle', { name: table.label })}
            </Typography>

            <TextField
                label={i18n.t('tableMaps.wizard.tableNumberName')}
                value={inputs.label}
                onChange={(e) => change({ label: e.target.value })}
                variant="filled"
                fullWidth
                inputProps={{ maxLength: TABLE_NAME_MAX_LENGTH }}
                error={!!errors.label}
                helperText={
                    errors.label &&
                    i18n.t(
                        errors.label === 'required'
                            ? 'tableMaps.wizard.nameRequired'
                            : errors.label === 'tooLong'
                            ? 'tableMaps.wizard.nameTooLong'
                            : 'tableMaps.wizard.nameDuplicate',
                        { max: TABLE_NAME_MAX_LENGTH }
                    )
                }
            />

            <div>
                <Typography className={classes.sectionLabel}>
                    {i18n.t('tableMaps.wizard.tables.assignedQrCode')}
                    {inputs.qrCode && !errors.qrInUse && (
                        <span className={classes.assignedChip}>
                            <CheckCircleOutlineIcon className={classes.assignedIcon} />
                            {i18n.t('tableMaps.wizard.tables.assigned')}
                        </span>
                    )}
                </Typography>
                <FormControl variant="filled" fullWidth error={errors.qrInUse}>
                    <InputLabel id="qr-label" shrink className={classes.qrLabel}>
                        {i18n.t('tableMaps.wizard.tables.selectQrCode')}
                    </InputLabel>
                    <Select
                        labelId="qr-label"
                        displayEmpty
                        value={inputs.qrCode ?? ''}
                        onChange={(e) => change({ qrCode: (e.target.value as string) || null })}
                    >
                        <MenuItem value="">{i18n.t('tableMaps.wizard.noQrLinked')}</MenuItem>
                        {availableQrCodes.map((qr) => (
                            <MenuItem key={qr.name} value={qr.name}>
                                {qr.name}
                            </MenuItem>
                        ))}
                    </Select>
                    {errors.qrInUse && (
                        <FormHelperText>{i18n.t('tableMaps.wizard.qrInUse')}</FormHelperText>
                    )}
                </FormControl>
                <FormHelperText>{i18n.t('tableMaps.wizard.tables.qrHelper')}</FormHelperText>
            </div>

            <div>
                <div className={classes.fieldRow}>
                    <FormControl variant="filled">
                        <InputLabel id="shape-label">{i18n.t('tableMaps.wizard.shape')}</InputLabel>
                        <Select
                            labelId="shape-label"
                            value={inputs.shape}
                            onChange={(e) => change({ shape: e.target.value as TableShape })}
                        >
                            {TABLE_SHAPES.map((shape) => (
                                <MenuItem key={shape} value={shape}>
                                    {i18n.t(TABLE_SHAPE_NAME_KEY[shape])}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        label={i18n.t('tableMaps.wizard.seats')}
                        value={inputs.seats}
                        onChange={(e) => change({ seats: e.target.value })}
                        variant="filled"
                        error={errors.seats}
                        inputProps={{ inputMode: 'numeric' }}
                        InputProps={{ endAdornment: renderSeatsStepper() }}
                    />
                </div>
                {errors.seats && (
                    <FormHelperText error>
                        {i18n.t('tableMaps.wizard.tables.seatsInvalid', { max: TABLE_SEATS_MAX })}
                    </FormHelperText>
                )}
            </div>

            <div>
                <Typography className={classes.sectionLabel}>
                    {i18n.t('tableMaps.wizard.tables.size')}
                    {isGeometryPending && (
                        <>
                            <span className={classes.pendingChip}>
                                {i18n.t('tableMaps.wizard.tables.pending').toUpperCase()}
                            </span>
                            <span className={classes.pendingNote}>
                                {i18n.t('tableMaps.wizard.tables.liveOnMap')}
                            </span>
                        </>
                    )}
                </Typography>
                <div className={classes.fieldRow}>
                    <TextField
                        label={i18n.t('tableMaps.wizard.tableWidth')}
                        value={inputs.width}
                        onChange={(e) => change({ width: e.target.value })}
                        variant="filled"
                        error={!!errors.width}
                        inputProps={{ inputMode: 'decimal' }}
                    />
                    <TextField
                        label={i18n.t('tableMaps.wizard.tableLength')}
                        value={inputs.height}
                        onChange={(e) => change({ height: e.target.value })}
                        variant="filled"
                        error={!!errors.height}
                        inputProps={{ inputMode: 'decimal' }}
                    />
                </div>
                {sizeMessage(errors.width, draft.size.width, 'tableMaps.wizard.tableWidth')}
                {sizeMessage(errors.height, draft.size.height, 'tableMaps.wizard.tableLength')}
            </div>

            <div>
                <div className={classes.rotationRow}>
                    <TextField
                        className={classes.rotationField}
                        label={i18n.t('tableMaps.wizard.rotation')}
                        value={inputs.rotation}
                        onChange={(e) => change({ rotation: e.target.value })}
                        variant="filled"
                        error={errors.rotation}
                        inputProps={{ inputMode: 'numeric' }}
                    />
                    <div className={classes.sliderBox}>
                        <Slider
                            value={edit.rotation}
                            min={0}
                            max={359}
                            step={1}
                            marks={[
                                { value: 0, label: '0°' },
                                { value: 359, label: '359°' },
                            ]}
                            onChange={(_, value) => change({ rotation: String(value) })}
                            aria-label={i18n.t('tableMaps.wizard.tables.rotate')}
                        />
                    </div>
                </div>
                {errors.rotation && (
                    <FormHelperText error>
                        {i18n.t('tableMaps.wizard.rotationInvalid')}
                    </FormHelperText>
                )}
            </div>

            <div>
                <FormControlLabel
                    control={
                        <Switch
                            color="primary"
                            checked={inputs.isActive}
                            disabled={!inputs.qrCode}
                            onChange={(e) => change({ isActive: e.target.checked })}
                        />
                    }
                    label={i18n.t('tableMaps.wizard.tableActive')}
                />
                {!inputs.qrCode && (
                    <FormHelperText className={classes.toggleHint}>
                        {i18n.t('tableMaps.wizard.tables.activeNeedsQr')}
                    </FormHelperText>
                )}
            </div>
            <div>
                <FormControlLabel
                    control={
                        <Checkbox
                            className={classes.lockCheckbox}
                            color="primary"
                            checked={inputs.isPositionLocked}
                            onChange={(e) => change({ isPositionLocked: e.target.checked })}
                        />
                    }
                    label={i18n.t('tableMaps.wizard.tables.positionLocked')}
                />
                <FormHelperText className={classes.toggleHint}>
                    {i18n.t('tableMaps.wizard.tables.activeHelper')}
                </FormHelperText>
            </div>

            <div>
                <div className={classes.actions}>
                    <Button
                        variant="contained"
                        color="primary"
                        disabled={!canApply}
                        onClick={() => dispatch({ type: 'APPLY_PENDING_TABLE' })}
                    >
                        {i18n.t('tableMaps.wizard.features.applyChanges')}
                    </Button>
                    <Button
                        variant="outlined"
                        disabled={!pending}
                        onClick={() => dispatch({ type: 'DISCARD_PENDING_TABLE' })}
                    >
                        {i18n.t('tableMaps.wizard.features.cancelChanges')}
                    </Button>
                </div>
                <FormHelperText>
                    {i18n.t('tableMaps.wizard.tables.appliesTo', { name: table.label })}
                </FormHelperText>
                {pending && (
                    <FormHelperText>{i18n.t('tableMaps.wizard.tables.pendingHint')}</FormHelperText>
                )}
            </div>

            <Button
                variant="outlined"
                className={classes.deleteButton}
                startIcon={<DeleteOutlineIcon />}
                onClick={onDelete}
            >
                {i18n.t('tableMaps.wizard.delete')}
            </Button>
        </div>
    );
}
